/**
 * The modal dialog every form in the app opens inside.
 *
 * The design handoff lists focus trapping, Escape and focus restoration as
 * acceptance criteria rather than deferred polish, and they are the three things
 * a hand-rolled overlay always misses. All three are here:
 *
 * - **Focus moves in** on open, to the first focusable element, and **back out**
 *   on close, to whatever had it before. Without restoration, closing a dialog
 *   opened from a table row dumps focus at the top of the document and a keyboard
 *   user loses their place in a 50-row queue.
 * - **Tab cycles inside.** Without a trap, Tab walks into the page behind the
 *   backdrop, where clicks do nothing and the user cannot tell why.
 * - **Escape closes**, and a backdrop click closes, but a click *inside* does not
 *   propagate — otherwise dragging to select text in a field closes the dialog.
 *
 * `aria-modal` + `role="dialog"` + a labelled title is the rest of the contract.
 *
 * ── WHY THE EFFECT'S DEPENDENCY ARRAY IS EMPTY — TRBUD-259 ──
 *
 * It was `[onClose]`, which is the obvious spelling: the effect calls `onClose`,
 * so list it. The trap is that a dependency array is compared with `Object.is`
 * and **two function objects are never `Object.is`-equal** — so a caller handing
 * this component a freshly created function on every render changes that
 * dependency on every render, and the effect tears itself down and sets itself up
 * again each time. Setting up again focuses the first control in the panel.
 *
 * Nearly every caller does exactly that, and none of them is written wrongly:
 * `NewWorkOrderDialog` declares `function requestClose()` in its render body and
 * passes `onClose={requestClose}`; across `apps/web` forty-three components
 * render this one, and the inline `onClose={() => …}` spelling appears
 * seventy-odd times. Every field inside a dialog is component-local `useState`,
 * so every KEYSTROKE re-renders the dialog — and focus jumped out of the field
 * being typed into, back to the top of the form, one character in. Eden found it
 * on an iPad, where losing focus also dismisses the iOS keyboard and typing
 * becomes impossible; nothing about it is iPad-specific. `main.tsx` sets
 * `refetchOnWindowFocus: true`, so merely returning to the browser tab re-renders
 * the screen behind an open dialog and steals focus out of whatever field held
 * it.
 *
 * So the effect runs **once on mount and once on unmount**, which is what "focus
 * in on open, focus out on close" has always meant — the old array was making a
 * mount-and-unmount effect fire on a keystroke. Escape still reaches the CURRENT
 * prop because the handler reads `onCloseRef.current` rather than the `onClose`
 * it closed over; the ref is refreshed by its own effect after every render. Do
 * not "fix" this at the call sites instead: that is a `useCallback` in
 * forty-three components that every future caller would have to remember, and a
 * caller that forgot would reintroduce the bug silently. The component has to
 * tolerate an unstable `onClose`, so it does.
 */
import { useEffect, useId, useRef, type ReactNode } from "react";
import { Blueprint } from "./primitives";

/**
 * What counts as focusable, for the mount focus and for the Tab trap.
 *
 * Exported so `Dialog.test.tsx` can ask the component's OWN selector which
 * elements are in the trap's candidate set, rather than keeping a second copy
 * that would drift the first time this string changes.
 *
 * ── EVERY ARM CARRIES `:not([tabindex="-1"])`, AND IT HAS TO ──
 *
 * This was one trailing `[tabindex]:not([tabindex="-1"])` arm, which reads as
 * "and exclude anything explicitly removed from the tab order" and is not what
 * a selector list means: the arms are an OR, so `button:not([disabled])`
 * matched a `tabindex="-1"` button on its own and the last arm never got a say.
 * Measured while adding the close control below — it turned up FIRST in
 * `querySelectorAll`, which is to say it would have taken the mount focus on
 * all 43 dialogs and become a Tab wrap target, exactly the defect the attribute
 * was added to prevent.
 *
 * With the negation on each arm the set matches what a browser does: an element
 * at `tabindex="-1"` is focusable by script and by a pointer, and is NOT in the
 * tab order, so it must not be a wrap target either.
 */
export const FOCUSABLE = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

export function Dialog({
  title,
  titleAside,
  children,
  actions,
  onClose,
  width = 440,
}: {
  title: string;
  titleAside?: ReactNode;
  children: ReactNode;
  actions: ReactNode;
  onClose: () => void;
  width?: number;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  /**
   * `onClose` in a ref, refreshed after every render.
   *
   * This is what lets the effect below depend on nothing — see the header note.
   * The refresh is an EFFECT rather than a plain `onCloseRef.current = onClose`
   * in the render body because assigning to a ref during render is a side effect
   * on a render React is allowed to throw away and re-run, and the value that
   * survives would then be a closure the user never saw.
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;

    // Focus the first real control, not the panel — a dialog that focuses its own
    // container reads as "dialog" to a screen reader and then says nothing about
    // what to do next.
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        // `.current`, never the captured `onClose` — this handler is installed
        // once, so the closure it was created in is the one from mount.
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !panel) return;

      const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const firstEl = focusable[0]!;
      const lastEl = focusable[focusable.length - 1]!;
      // Wrap at both ends. Only handling the forward edge lets Shift+Tab escape.
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    // The page behind must not scroll under the backdrop.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
    // Deliberately empty — mount and unmount only. See the header note; an
    // `[onClose]` here re-focuses the top of the form on every keystroke.
  }, []);

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => {
        // Only a press that starts on the backdrop dismisses. Dragging to select
        // text and releasing outside must not throw away a half-filled form.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <Blueprint
        ref={panelRef}
        className="dialog"
        style={{ width: `min(${width}px, 100%)` }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        // Stop a click inside from reaching the backdrop's close handler.
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
      >
        <div className="dialog-layout">
          {/* `dialog-head` pins the title while the layout scrolls -- see the
              CSS note on `.dialog-layout` for why the LAYOUT is the scroller. */}
          <div className="row-baseline dialog-head">
            <span className="dialog-title" id={titleId}>
              {title}
            </span>
            {titleAside}
            {/*
              A visible way out of every dialog — WP5, 18 September 2026.

              Escape and a backdrop press already closed, and neither is
              discoverable: an APPROVED purchase order renders an EMPTY actions
              row (`NewPurchaseOrderDialog.tsx`, where every footer control is
              gated on DRAFT or SUBMITTED), so until this button existed that
              dialog had no control at all that shut it.

              ── `tabIndex={-1}` IS LOAD-BEARING, NOT A TIDY-UP ──

              The mount effect focuses `panel.querySelector(FOCUSABLE)` — the
              first match in DOCUMENT ORDER over the whole panel — and
              `dialog-head` precedes `dialog-body`. So any ordinarily focusable
              node here is focused ahead of the first field, on all 43 dialogs,
              and the person opening one lands on ✕ instead of on the form.
              (Today's `titleAside` users get away with it only because none of
              the five contains a focusable node.) `FOCUSABLE` excludes
              `[tabindex="-1"]` on every arm — see the note there, because it
              did NOT until this button was measured against it — so this one is
              invisible to BOTH the mount focus and the Tab trap while staying
              clickable, labelled and reachable by a screen reader's own element
              navigation.

              `onCloseRef.current()`, never the captured `onClose`, for the same
              reason Escape does it: see the header note.
            */}
            <button
              type="button"
              className="btn btn-ghost btn-icon dialog-close"
              aria-label="Close"
              title="Close"
              tabIndex={-1}
              onClick={() => onCloseRef.current()}
            >
              ✕
            </button>
          </div>
          <div className="dialog-body">{children}</div>
          <div className="dialog-actions">{actions}</div>
        </div>
      </Blueprint>
    </div>
  );
}

/**
 * The error line shown next to the control that failed.
 *
 * A separate component because the message has to be *where the action was*: a
 * toast that appears behind a modal is invisible, and a banner at the top of a
 * long screen is missed. Exported as `InlineError` too, since screens use it for
 * failed mutations outside dialogs.
 */
export function DialogError({ error }: { error: Error | null }) {
  if (!error) return null;
  return (
    <p
      role="alert"
      style={{
        fontSize: "var(--tb-text-sm)",
        margin: "var(--space-2) 0 0",
        padding: "var(--space-2)",
        border: "1px solid var(--color-accent-700)",
        color: "var(--color-text)",
      }}
    >
      {error.message}
    </p>
  );
}

/** Same component, named for its use outside a dialog. */
export { DialogError as InlineError };
